import { ComponentProps, useState, type FC } from 'react';

import { JobList } from 'components';
import { useJobSearch } from 'helpers';
import { Condition, JobSearchResult } from 'types';
import { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { format, parse } from 'date-fns';

type Props = Readonly<{
  technicianId?: string;
}>;

export const TechnicianJobs: FC<Props> = (props) => {
  const { technicianId } = props;
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(5);

  const [jobs, total = 0] = useJobSearch({
    searchBy: 'technician_id',
    searchValue: technicianId,
    page,
    recordsPerPage,
  });

  const paginationMetadata: ComponentProps<typeof JobList>['paginationMetadata'] = {
    current_page: page,
    onChange(page, pageSize) {
      setPage(page);
      setRecordsPerPage(pageSize);
    },
    pageSize: recordsPerPage,
    total,
  };

  const columns = [
    {
      key: 'jobId',
      title: 'Job ID',
      dataIndex: 'ID',
      render: (_, record) => <Link to={`/job/edit/${record.ID}/summary`}>{record.ID}</Link>,
      width: 50,
    },
    {
      key: 'booked',
      title: 'Booked',
      render: (_, record) => {
        return (
          <>
            {record.booking && (
              <div>{format(parse(record.booking, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy HH:mm')}</div>
            )}
          </>
        );
      },
      width: 100,
    },
    {
      key: 'appointment',
      title: 'Appointment',
      render: (_, record) => {
        const isCancelled = record.cancelled === Condition.Yes;
        return (
          <>
            {record.appointment && !isCancelled && (
              <div>{format(parse(record.appointment, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy HH:mm')}</div>
            )}
          </>
        );
      },
      width: 100,
    },
    {
      key: 'customerName',
      title: 'Customer',
      render: (_, record) => <span data-dd-privacy="mask">{record.customer_name}</span>,
      width: 100,
    },
    {
      key: 'descrioption',
      title: 'Description',
      render: (_, record) => <div title={record.description}>{record.description}</div>,
      width: 260,
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => (
        <div>
          {record.cancelled === Condition.Yes ? 'Cancelled' : record.completed === Condition.Yes ? 'Completed' : 'Open'}
        </div>
      ),
      width: 70,
    },
  ] as const satisfies ColumnsType<JobSearchResult>;

  return (
    <>
      <title>Technician Jobs</title>
      <JobList
        jobs={jobs}
        paginationMetadata={paginationMetadata}
        columnsDefinition={columns}
        hideColumns={['actions']}
      />
    </>
  );
};
